import React from 'react';
import { resolveImagePreloadSrc } from '../../../sourcing/data/contentstack/asset-helpers';

const DEFAULT_PRELOAD_TIMEOUT_MS = 10_000;

export type PreloadImageInput = {
    key: string;
    image?: Partial<ContentStackImage> | null;
};

export type UsePreloadReadyOptions = {
    resolveSrc?: (image: Partial<ContentStackImage>) => string | null;
    defaultWidth?: number;
    timeoutMs?: number;
};

export type UsePreloadReadyResult = {
    ready: boolean;
    needsPreload: boolean;
};

export const resolveContentStackPreloadSrc = resolveImagePreloadSrc;

export const usePreloadReady = (
    items: PreloadImageInput[],
    options?: UsePreloadReadyOptions,
): UsePreloadReadyResult => {
    const customResolveSrc = options?.resolveSrc;
    const defaultWidth = options?.defaultWidth;
    const resolveSrc = React.useCallback(
        (image: Partial<ContentStackImage>) => {
            if (customResolveSrc) {
                return customResolveSrc(image);
            }
            return resolveImagePreloadSrc(image, { defaultWidth });
        },
        [customResolveSrc, defaultWidth],
    );
    const timeoutMs = options?.timeoutMs ?? DEFAULT_PRELOAD_TIMEOUT_MS;

    const itemsKey = items.map(({ key }) => key).join('|');
    const itemCount = items.length;
    const [ready, setReady] = React.useState(itemCount === 0);

    React.useEffect(() => {
        if (itemCount === 0) {
            setReady(true);
            return undefined;
        }

        setReady(false);
        let cancelled = false;
        const pending = new Set(items.map(({ key }) => key));

        const markDone = (key: string): void => {
            if (cancelled || !pending.has(key)) {
                return;
            }
            pending.delete(key);
            if (pending.size === 0) {
                setReady(true);
            }
        };

        items.forEach(({ key, image }) => {
            if (!image) {
                markDone(key);
                return;
            }
            const src = resolveSrc(image);
            if (!src) {
                markDone(key);
                return;
            }
            const el = new window.Image();
            el.onload = () => markDone(key);
            el.onerror = () => markDone(key);
            el.src = src;
            if (el.complete) {
                markDone(key);
            }
        });

        const timeoutId = window.setTimeout(() => {
            if (cancelled || pending.size === 0) {
                return;
            }
            pending.clear();
            setReady(true);
        }, timeoutMs);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    // itemsKey serializes inputs; items may be unstable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on deduped keys only
    }, [itemsKey, itemCount, resolveSrc, timeoutMs]);

    return {
        ready,
        needsPreload: itemCount > 0,
    };
};
